import React, { Component } from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, Tag } from "antd";
import LoadingWrapper from "../../common/loading-wrapper";
import { toDateString, toPatentDetailLink, toItemPTABSearchLink, toGooglePatentLink, toCaseDetailLink, toJudgeDetailLink, colorPTABStatus } from "../../../helpers/converters";
import { generateCellContent } from "../../../helpers/table";

const styles = {
  fieldHeader: {
    color: "gray",
    marginTop: 16,
    marginBottom: 0
  },
  addonTag: {
    cursor: "default"
  }
};

class CaseDetails extends Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired
  };

  render() {
    const INDUSTRY_MAP = {
      T: "High-Tech",
      M: "Medical",
      O: "Other"
    };
    const PET_TYPE_MAP = {
      LOC: "Large Operating Company",
      SME: "SME",
      UP: "Unified Patents",
      DA: "Defensive Aggregators",
      O: "Other",
      G: "Government",
      U: "University"
    };
    const patentNumberRenderer = (val, record) => {
      if (val) {
        let res = [toPatentDetailLink(val), toItemPTABSearchLink(val, null, "patent.patent_number"), toGooglePatentLink(`US${val}`)];
        if (record.up_industry) {
          res.push(" ");
          res.push(
            <Tag color="cyan" key="up_industry" style={styles.addonTag}>
              {INDUSTRY_MAP[record.up_industry]}
            </Tag>
          );
        }
        return res;
      } else {
        return val;
      }
    };
    const entitiesRenderer = (val, record, key) => {
      return val ? Array.from(val).map((v, i) => [v, toItemPTABSearchLink(v, null, key), <br key={i} />]) : [];
    };
    const judgeRenderer = (val, record, key) => {
      return val ? Array.from(val).map((v, i) => [toJudgeDetailLink(v, key.replace("judges.", "")), toItemPTABSearchLink(v, null, key), <br key={i} />]) : [];
    };
    const patentOwnerRenderer = (val, record, key) => {
      let res = entitiesRenderer(val, record, key);
      if (record.up_po_type) {
        res.push(
          <Tag color="orange" key="up_po_type" style={styles.addonTag}>
            {record.up_po_type}
          </Tag>
        );
      }
      return res;
    };
    const petitionerRenderer = (val, record, key) => {
      let res = entitiesRenderer(val, record, key);
      if (record.up_pet_type) {
        res.push(
          <Tag color="orange" key="up_pet_type" style={styles.addonTag}>
            {PET_TYPE_MAP[record.up_pet_type]}
          </Tag>
        );
      }
      return res;
    };
    const statusRenderer = (val, record) => {
      let res = [colorPTABStatus(val)];
      if (record.parent_case) {
        res.push(<br key={1} />);
        res.push(<span key="parent_case">{"( Joined with "}</span>, toCaseDetailLink(record.parent_case), <span key={4}>{" )"}</span>);
      }
      if (record.is_on_appeal && !record.is_on_remand) {
        res.push(" ");
        res.push(
          <Tag color="gold" key="is_on_appeal" style={styles.addonTag}>
            {"Appealed"}
          </Tag>
        );
      }
      if (record.is_on_remand) {
        res.push(" ");
        res.push(
          <Tag color="gold" key="is_on_remand" style={styles.addonTag}>
            {"Remanded"}
          </Tag>
        );
      }
      if (record.discretionary_denial_statutes && record.discretionary_denial_statutes[0]) {
        const re = /(\d{3})(\w)/g;
        const match = re.exec(record.discretionary_denial_statutes[0]);
        res.push(" ");
        res.push(
          <Tag color="blue" key="discretionary_denial_statutes" style={styles.addonTag}>
            {`§ ${match[1]}(${match[2]})`}
          </Tag>
        );
      }
      return res;
    };
    const patentHeaders = {
      "patent.patent_number": {
        name: "PATENT NUMBER",
        converter: patentNumberRenderer
      },
      ptab_appl_number: {
        name: "APPL. NUMBER"
      }
    };
    const partyHeaders = {
      petitioners: {
        name: "PETITIONER",
        converter: petitionerRenderer
      },
      patent_owners: {
        name: "PATENT OWNER",
        converter: patentOwnerRenderer
      }
    };
    const statusHeaders = {
      up_status_concat: {
        name: "STATUS",
        converter: statusRenderer
      },
      filing_date: {
        name: "FILING DATE",
        converter: toDateString
      },
      up_institution_date: {
        name: "INSTITUTION DATE",
        converter: toDateString
      },
      up_termination_date: {
        name: "TERMINATION DATE",
        converter: toDateString
      }
    };
    const lawfirmHeaders = {
      "lawfirms.petitioner": {
        name: "PET. COUNSEL",
        converter: entitiesRenderer
      },
      "lawfirms.patent_owner": {
        name: "P.O. COUNSEL",
        converter: entitiesRenderer
      }
    };
    const judgeHeaders = {
      "judges.author": {
        name: "AUTHOR JUDGE",
        converter: judgeRenderer
      },
      "judges.panel": {
        name: "PANEL JUDGE",
        converter: judgeRenderer
      }
    };
    const { loading, data } = this.props;
    return (
      <Card title="Details">
        <LoadingWrapper loading={loading}>
          <Row gutter={8}>
            <Col span={24} lg={8}>
              {this.renderFields(patentHeaders, data)}
              {this.renderFields(partyHeaders, data)}
            </Col>
            <Col span={24} lg={6}>
              {this.renderFields(statusHeaders, data)}
            </Col>
            <Col span={24} lg={10}>
              {this.renderFields(lawfirmHeaders, data)}
              {this.renderFields(judgeHeaders, data)}
            </Col>
          </Row>
        </LoadingWrapper>
      </Card>
    );
  }

  renderFields = (headers, data) => {
    return Object.entries(headers).map(([headerKey, headerObj], headerIndex) => {
      let val = generateCellContent(headerKey, headerObj, data);
      return (
        <div key={headerIndex}>
          <h6 style={styles.fieldHeader}>{headerObj.name}</h6>
          <div>{val}</div>
        </div>
      );
    });
  };
}

export default CaseDetails;
